/**
 * Drawer-return coordination (native only).
 *
 * Most drawer destinations are screens *of* the drawer navigator (chat, cv,
 * jobs, skills, connectors, profile, analytics, candidates), so their leading
 * chevron simply re-opens the drawer — see `(app)/cv/_layout.tsx` and friends.
 * That is why backing out of Find Jobs already lands on the drawer.
 *
 * The rest — Inbox, Notifications, Automation, Memory, Upgrade, Settings — are
 * root-stack routes that sit *above* the drawer navigator. Dismissing one only
 * popped the root stack, revealing whichever drawer screen happened to be
 * mounted underneath (Find Jobs, if that is where the user had been), instead
 * of the drawer they opened it from.
 *
 * This module closes that gap without moving any routes:
 *   1. the drawer records the root-stack route it is about to push;
 *   2. the root stack's `beforeRemove` listener asks here whether the screen
 *      being dismissed is that route, and re-opens the drawer when it is.
 *
 * Because the mark is set at push time, routes reached any other way — a push
 * notification, a deep link, an in-app link from some other screen — are never
 * marked and keep their ordinary back behaviour.
 *
 * Deliberately free of `react-native` / `@react-navigation` imports so the
 * decision logic stays unit-testable, and so the web bundle (which has a
 * sidebar, not a drawer) can never accidentally open a drawer that is not
 * there: nothing registers an opener on web.
 */

/**
 * Navigation actions that mean "the user went back", as opposed to the screen
 * being swapped out by a redirect. iOS swipe-back and the Android hardware back
 * button both arrive as `POP` (react-navigation's native stack dispatches
 * `StackActions.pop()` from its native `onDismissed`), the header chevron's
 * `router.back()` arrives as `GO_BACK`.
 */
export const DRAWER_RETURN_BACK_ACTIONS = [
  'GO_BACK',
  'POP',
  'POP_TO',
  'POP_TO_TOP',
] as const;

const BACK_ACTION_TYPES = new Set<string>(DRAWER_RETURN_BACK_ACTIONS);

/**
 * First path segment of an href, which is also the root-stack screen name that
 * hosts it: `/settings/memory` → `settings`, `/messages` → `messages`.
 */
export function getRootStackSegment(href: string): string | null {
  const [path = ''] = href.split(/[?#]/);
  const [segment = ''] = path.replace(/^\/+/, '').split('/');
  return segment || null;
}

/**
 * Pure decision helper: should dismissing `routeName` via `actionType` hand the
 * user back to the drawer, given the route the drawer marked?
 *
 * An unknown/absent action type counts as a back action — every dismissal path
 * we know of reports one, and falling back to "yes" keeps the drawer reachable
 * if a future navigator reports something new.
 */
export function isDrawerReturnDismissal(
  pendingSegment: string | null,
  routeName: string,
  actionType?: string | null,
): boolean {
  if (!pendingSegment || pendingSegment !== routeName) {
    return false;
  }
  if (!actionType) {
    return true;
  }
  return BACK_ACTION_TYPES.has(actionType);
}

// ─── Pending-return state ────────────────────────────────────────────

let pendingReturnSegment: string | null = null;

/**
 * Called by the drawer right before it pushes a route that lives outside the
 * drawer navigator. Only one route can be marked at a time: a second push
 * replaces the first, which is what we want — the drawer only ever launches one
 * screen per tap.
 */
export function markPushedFromDrawer(href: string): void {
  pendingReturnSegment = getRootStackSegment(href);
}

/**
 * Drops the mark. Used when the user reaches a drawer destination some other
 * way (switching drawer screens, starting a new chat), so a stale mark cannot
 * pop the drawer open later.
 */
export function clearDrawerReturn(): void {
  pendingReturnSegment = null;
}

export function getPendingDrawerReturnSegment(): string | null {
  return pendingReturnSegment;
}

/**
 * Stateful entry point for the root stack's `beforeRemove` listener. Returns
 * whether the drawer should be re-opened, and always drops the mark once the
 * marked route is gone (even when it left via a redirect rather than a back
 * action) so it cannot fire against an unrelated screen later.
 */
export function resolveDrawerReturnOnRemove(
  routeName: string,
  actionType?: string | null,
): boolean {
  if (!pendingReturnSegment || pendingReturnSegment !== routeName) {
    return false;
  }

  const shouldOpenDrawer = isDrawerReturnDismissal(
    pendingReturnSegment,
    routeName,
    actionType,
  );
  pendingReturnSegment = null;
  return shouldOpenDrawer;
}

// ─── Drawer opener registry ──────────────────────────────────────────

type DrawerOpener = () => void;

let activeDrawerOpener: DrawerOpener | null = null;

/**
 * Publishes how to open the drawer. `DrawerActions.openDrawer()` cannot simply
 * be dispatched from a root-stack screen: the drawer is a *descendant* of the
 * root stack, and a screen's actions only bubble up to its parents.
 *
 * Registered by the root layout, not by the drawer itself: the drawer subtree
 * is frozen while a pushed screen covers it (`freezeOnBlur`), which can tear
 * its effects down — exactly when we need the opener. The root layout is
 * mounted for the whole session, so its registration cannot go stale.
 */
export function registerDrawerOpener(opener: DrawerOpener): void {
  activeDrawerOpener = opener;
}

export function unregisterDrawerOpener(opener: DrawerOpener): void {
  if (activeDrawerOpener === opener) {
    activeDrawerOpener = null;
  }
}

/** Returns whether an opener was registered to receive the action. */
export function openAppDrawer(): boolean {
  if (!activeDrawerOpener) {
    return false;
  }
  activeDrawerOpener();
  return true;
}
